import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import PageTransition from '@/components/ui/PageTransition';
import { Card, Button, Flex, Typography, Tag, Spin, Empty, theme } from 'antd';
import {
  ArrowLeftOutlined,
  ClockCircleOutlined,
  ExceptionOutlined,
  SettingOutlined,
  SyncOutlined
} from '@ant-design/icons';
import {
  useGetResourceQuery,
  useGetResourcePolicyQuery
} from '@/api/mcp-appointments';
import { ResourceTypeIcon } from '@/components/resources/ResourceTypeIcon';
import { DetailPageHeader } from '@/components/ui/DetailPageHeader';
import {
  AvailabilitySettings,
  CalendarsSyncSettings,
  ExceptionsSettings,
  PolicySettings,
  SettingsTabsCard
} from '@/components/resources/settings';
import { createManageRouteGuard } from '@/utils/manageRouteGuards';
import { TENANT_ROLES } from '@/constants/tenantRoles';
import { getResourceDisplayName } from '@/utils/serviceResourceLinks';
import { tabSearchParam } from '@/utils/searchParams';

const { Text } = Typography;

const SETTINGS_TABS = ['availability', 'exceptions', 'policy', 'calendars-sync'] as const;
type SettingsTab = (typeof SETTINGS_TABS)[number];
const DEFAULT_SETTINGS_TAB: SettingsTab = 'availability';

export const Route = createFileRoute('/manage/company-resources/$resourceId/settings')({
  beforeLoad: createManageRouteGuard({ requiredTenantRoles: [TENANT_ROLES.OWNER] }),
  validateSearch: (search: Record<string, unknown>) => ({
    tab: tabSearchParam(search.tab, SETTINGS_TABS, DEFAULT_SETTINGS_TAB)
  }),
  component: ResourceSettingsPage
});

function ResourceSettingsPage() {
  const { t } = useTranslation('manage');
  const { resourceId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { tab } = Route.useSearch();
  const { token } = theme.useToken();

  // Queries
  const { data: resource, isPending: resourcePending } = useGetResourceQuery(resourceId);
  // Policy query - 404 is handled gracefully (no policy exists yet)
  useGetResourcePolicyQuery(resourceId);

  if (resourcePending) {
    return (
      <PageTransition>
        <Flex
          vertical
          align="center"
          justify="center"
          gap="middle"
          style={{ minHeight: '60vh', textAlign: 'center' }}
        >
          <Spin size="large" />
          <Text type="secondary" style={{ fontSize: token.fontSize }}>
            {t('resourceSettings.loading')}
          </Text>
        </Flex>
      </PageTransition>
    );
  }

  if (!resource) {
    return (
      <PageTransition>
        <Flex vertical gap="middle" style={{ paddingTop: token.padding }}>
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Flex vertical align="center" gap="middle" style={{ marginTop: token.marginMD }}>
                  <Text type="secondary" style={{ fontSize: token.fontSizeLG }}>
                    {t('resourceSettings.notFound')}
                  </Text>
                  <Button
                    type="primary"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate({ to: '/manage/company-resources' })}
                  >
                    {t('resourceSettings.backToResources')}
                  </Button>
                </Flex>
              }
            />
          </Card>
        </Flex>
      </PageTransition>
    );
  }

  const resourceName = getResourceDisplayName(resource, t('resources.table.unnamedResource'));

  return (
    <PageTransition>
      <Flex vertical gap={0} style={{ paddingTop: 0 }}>
        <DetailPageHeader
          icon={<ResourceTypeIcon type={resource.type} />}
          title={resourceName}
          meta={
            <>
              <Tag color="blue">{t(`resources.filters.${resource.type.toLowerCase()}`)}</Tag>
              <Text type="secondary">{resource.timezone}</Text>
            </>
          }
          backTo="/manage/company-resources"
          backLabel={t('resourceSettings.backToResources')}
        />

        <SettingsTabsCard
          activeKey={tab}
          onChange={(nextTab) =>
            (navigate as any)({
              to: '/manage/company-resources/$resourceId/settings',
              params: { resourceId },
              search: { tab: nextTab as SettingsTab }
            })
          }
          items={[
            {
              key: 'availability',
              icon: <ClockCircleOutlined />,
              label: t('resourceSettings.tabs.availability'),
              children: <AvailabilitySettings resourceId={resourceId} resource={resource} />
            },
            {
              key: 'exceptions',
              icon: <ExceptionOutlined />,
              label: t('resourceSettings.tabs.exceptions'),
              children: <ExceptionsSettings resourceId={resourceId} />
            },
            {
              key: 'policy',
              icon: <SettingOutlined />,
              label: t('resourceSettings.tabs.policy'),
              children: <PolicySettings resourceId={resourceId} />
            },
            {
              key: 'calendars-sync',
              icon: <SyncOutlined />,
              label: t('resourceSettings.tabs.calendarsSync'),
              children: <CalendarsSyncSettings resourceId={resourceId} resource={resource} />
            }
          ]}
        />
      </Flex>
    </PageTransition>
  );
}
